import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Button, Tag, Space, Modal, Descriptions, Input,
  message, Typography, Alert, Row, Col,
} from 'antd';
import {
  FileTextOutlined, StopOutlined, SearchOutlined, EyeOutlined,
  SendOutlined, ExclamationCircleOutlined, ReloadOutlined, ClearOutlined,
} from '@ant-design/icons';
import fiscalService from '../../services/fiscalService';

const { Text } = Typography;

const STATUS_COLOR = {
  RPS_PENDENTE: 'blue',
  VALIDADO: 'cyan',
  ENVIADO: 'geekblue',
  PROCESSANDO: 'purple',
  EMITIDO: 'green',
  ERRO: 'red',
  CANCELADO: 'default',
  SUBSTITUIDO: 'orange',
};

const FILTROS_VAZIOS = {
  competencia: '',
  competencia_de: '',
  competencia_ate: '',
  busca: '',
  numero_rps: '',
  numero_nfse: '',
  id_fatura: '',
};

const NotasFiscais = () => {
  const [rps, setRps] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [filtroStatus, setFiltroStatus] = useState('');

  // Filtros aplicados (disparam o fetch)
  const [filtros, setFiltros] = useState(FILTROS_VAZIOS);
  // Rascunho dos inputs
  const [draft, setDraft] = useState(FILTROS_VAZIOS);

  // Ordenação
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState(null);

  const [detalheRps, setDetalheRps] = useState(null);
  const [detalheVisible, setDetalheVisible] = useState(false);
  const [cancelandoId, setCancelandoId] = useState(null);
  const [emitindoId, setEmitindoId] = useState(null);
  const [reenviandoId, setReenviandoId] = useState(null);

  const fetchRps = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, size: 50 };
      if (filtroStatus)            params.status         = filtroStatus;
      if (filtros.competencia)     params.competencia     = filtros.competencia;
      if (filtros.competencia_de)  params.competencia_de  = filtros.competencia_de;
      if (filtros.competencia_ate) params.competencia_ate = filtros.competencia_ate;
      if (filtros.busca)           params.busca           = filtros.busca;
      if (filtros.numero_rps)      params.numero_rps      = filtros.numero_rps;
      if (filtros.numero_nfse)     params.numero_nfse     = filtros.numero_nfse;
      if (filtros.id_fatura)       params.id_fatura       = parseInt(filtros.id_fatura, 10);
      if (sortBy) {
        params.sort_by  = sortBy;
        params.sort_dir = sortDir || 'desc';
      }
      const resp = await fiscalService.listarRps(params);
      setRps(resp.data.items);
      setTotal(resp.data.total);
    } catch (err) {
      const detail = err.response?.data?.detail;
      message.error(typeof detail === 'string' ? detail : 'Erro ao carregar RPS.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, filtroStatus, filtros, sortBy, sortDir]);

  useEffect(() => {
    fetchRps();
  }, [fetchRps]);

  const handleBuscar = () => {
    setFiltros({ ...draft });
    setPage(1);
  };

  const handleLimpar = () => {
    setDraft(FILTROS_VAZIOS);
    setFiltros(FILTROS_VAZIOS);
    setFiltroStatus('');
    setSortBy(null);
    setSortDir(null);
    setPage(1);
  };

  const handleDraft = (campo, valor) => {
    setDraft(prev => {
      const novo = { ...prev, [campo]: valor };
      // Exclusividade: competência exata vs intervalo
      if (campo === 'competencia' && valor) {
        novo.competencia_de = '';
        novo.competencia_ate = '';
      } else if ((campo === 'competencia_de' || campo === 'competencia_ate') && valor) {
        novo.competencia = '';
      }
      return novo;
    });
  };

  const handleTableChange = (_pagination, _filters, sorter) => {
    const novoSortBy  = sorter?.columnKey && sorter?.order ? sorter.columnKey : null;
    const novoSortDir = sorter?.order === 'ascend' ? 'asc' : (sorter?.order === 'descend' ? 'desc' : null);
    const sortouMudou = novoSortBy !== sortBy || novoSortDir !== sortDir;
    setSortBy(novoSortBy);
    setSortDir(novoSortDir);
    if (sortouMudou) {
      setPage(1);
    } else {
      setPage(_pagination.current);
    }
  };

  const handleVerDetalhe = async (id) => {
    try {
      const resp = await fiscalService.obterRps(id);
      setDetalheRps(resp.data);
      setDetalheVisible(true);
    } catch {
      message.error('Erro ao carregar detalhes do RPS.');
    }
  };

  const handleCancelar = (record) => {
    Modal.confirm({
      title: `Cancelar RPS #${record.id}?`,
      content: (
        <div>
          <p>Esta ação é irreversível. O RPS ficará com status CANCELADO.</p>
          <p>Uma nova emissão poderá ser feita para a mesma fatura após o cancelamento.</p>
        </div>
      ),
      okText: 'Cancelar RPS',
      cancelText: 'Voltar',
      okButtonProps: { danger: true },
      onOk: async () => {
        setCancelandoId(record.id);
        try {
          await fiscalService.cancelarRps(record.id, 'Cancelado pelo usuário via sistema.');
          message.success(`RPS #${record.id} cancelado com sucesso.`);
          await fetchRps();
        } catch (err) {
          const detail = err.response?.data?.detail;
          message.error(typeof detail === 'string' ? detail : 'Erro ao cancelar RPS.');
        } finally {
          setCancelandoId(null);
        }
      },
    });
  };

  const handleEmitir = (record) => {
    Modal.confirm({
      title: (
        <span style={{ color: '#d46b08' }}>
          <ExclamationCircleOutlined style={{ marginRight: 8 }} />
          Emitir NFS-e REAL — Ação Irreversível
        </span>
      ),
      icon: null,
      content: (
        <div>
          <Alert
            type="warning"
            showIcon
            message="Esta emissão é REAL e definitiva"
            description={
              <ul style={{ margin: '8px 0 0 0', paddingLeft: 16 }}>
                <li>A NFS-e será registrada na Prefeitura de São Paulo.</li>
                <li>Não existe ambiente de homologação — não é um teste.</li>
                <li>Após emitida, a nota só pode ser cancelada via processo formal.</li>
                <li>O número RPS <strong>{record.numero_rps}/{record.serie_rps}</strong> será consumido definitivamente.</li>
              </ul>
            }
            style={{ marginBottom: 12 }}
          />
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <tbody>
              <tr>
                <td style={{ padding: '3px 8px 3px 0', fontWeight: 600, whiteSpace: 'nowrap', width: 140 }}>Nº/Série RPS</td>
                <td style={{ padding: '3px 0' }}>{record.numero_rps} / {record.serie_rps}</td>
              </tr>
              <tr>
                <td style={{ padding: '3px 8px 3px 0', fontWeight: 600 }}>Tomador</td>
                <td style={{ padding: '3px 0' }}>{record.tomador_nome}</td>
              </tr>
              {record.tomador_cpf_cnpj && (
                <tr>
                  <td style={{ padding: '3px 8px 3px 0', fontWeight: 600 }}>CPF/CNPJ</td>
                  <td style={{ padding: '3px 0' }}>{record.tomador_cpf_cnpj}</td>
                </tr>
              )}
              <tr>
                <td style={{ padding: '3px 8px 3px 0', fontWeight: 600 }}>Valor</td>
                <td style={{ padding: '3px 0' }}>
                  {parseFloat(record.valor_servicos).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </td>
              </tr>
              <tr>
                <td style={{ padding: '3px 8px 3px 0', fontWeight: 600 }}>Cód. Serviço</td>
                <td style={{ padding: '3px 0' }}>{record.prestador_codigo_servico}</td>
              </tr>
              <tr>
                <td style={{ padding: '3px 8px 3px 0', fontWeight: 600, verticalAlign: 'top' }}>Discriminação</td>
                <td style={{ padding: '3px 0', maxHeight: 60, overflow: 'hidden', display: 'block', color: '#595959' }}>
                  {record.discriminacao?.length > 120
                    ? record.discriminacao.slice(0, 120) + '…'
                    : record.discriminacao}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      ),
      okText: 'Confirmar Emissão Real',
      cancelText: 'Cancelar',
      okButtonProps: { danger: false, type: 'primary' },
      width: 520,
      onOk: async () => {
        setEmitindoId(record.id);
        try {
          const resp = await fiscalService.emitirRps(record.id);
          const rpsAtualizado = resp.data;
          if (rpsAtualizado.status === 'EMITIDO') {
            message.success(
              `NFS-e ${rpsAtualizado.numero_nfse} emitida com sucesso! Código: ${rpsAtualizado.codigo_verificacao}`,
              8,
            );
          } else {
            const ultimoEvento = rpsAtualizado.eventos?.[rpsAtualizado.eventos.length - 1];
            message.error(
              `Prefeitura retornou erro: ${ultimoEvento?.mensagem || 'Sem detalhes.'}`,
              10,
            );
          }
          await fetchRps();
        } catch (err) {
          const detail = err.response?.data?.detail;
          message.error(
            `Falha técnica: ${typeof detail === 'string' ? detail : 'Erro ao emitir NFS-e.'}`,
            10,
          );
          await fetchRps();
        } finally {
          setEmitindoId(null);
        }
      },
    });
  };

  const handleReenviar = (record) => {
    Modal.confirm({
      title: (
        <span style={{ color: '#d46b08' }}>
          <ReloadOutlined style={{ marginRight: 8 }} />
          Tentar novamente — Reenvio de RPS rejeitado
        </span>
      ),
      icon: null,
      content: (
        <div>
          <Alert
            type="warning"
            showIcon
            message="Este RPS já foi rejeitado anteriormente pela Prefeitura"
            description={
              <ul style={{ margin: '8px 0 0 0', paddingLeft: 16 }}>
                <li>Será reenviado com o <strong>mesmo número {record.numero_rps}/{record.serie_rps}</strong>.</li>
                <li>A ação é permitida porque <strong>nenhuma NFS-e foi emitida</strong> para este RPS.</li>
                <li>Não clique novamente enquanto o processamento estiver em andamento.</li>
                <li>Em caso de nova rejeição, o status voltará a ERRO com os novos detalhes.</li>
              </ul>
            }
            style={{ marginBottom: 12 }}
          />
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <tbody>
              <tr>
                <td style={{ padding: '3px 8px 3px 0', fontWeight: 600, whiteSpace: 'nowrap', width: 140 }}>Nº/Série RPS</td>
                <td style={{ padding: '3px 0' }}>{record.numero_rps} / {record.serie_rps}</td>
              </tr>
              <tr>
                <td style={{ padding: '3px 8px 3px 0', fontWeight: 600 }}>Tomador</td>
                <td style={{ padding: '3px 0' }}>{record.tomador_nome}</td>
              </tr>
              <tr>
                <td style={{ padding: '3px 8px 3px 0', fontWeight: 600 }}>Valor</td>
                <td style={{ padding: '3px 0' }}>
                  {parseFloat(record.valor_servicos).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      ),
      okText: 'Reenviar para Prefeitura',
      cancelText: 'Cancelar',
      okButtonProps: { type: 'primary' },
      width: 520,
      onOk: async () => {
        setReenviandoId(record.id);
        try {
          const resp = await fiscalService.reenviarRps(record.id);
          const rpsAtualizado = resp.data;
          if (rpsAtualizado.status === 'EMITIDO') {
            message.success(
              `NFS-e ${rpsAtualizado.numero_nfse} emitida com sucesso! Código: ${rpsAtualizado.codigo_verificacao}`,
              8,
            );
          } else {
            const ultimoEvento = rpsAtualizado.eventos?.[rpsAtualizado.eventos.length - 1];
            message.error(
              `Prefeitura retornou erro: ${ultimoEvento?.mensagem || 'Sem detalhes.'}`,
              10,
            );
          }
          await fetchRps();
        } catch (err) {
          const detail = err.response?.data?.detail;
          message.error(
            `Falha técnica: ${typeof detail === 'string' ? detail : 'Erro ao reenviar RPS.'}`,
            10,
          );
          await fetchRps();
        } finally {
          setReenviandoId(null);
        }
      },
    });
  };

  const sortOrder = (key) => sortBy === key ? (sortDir === 'asc' ? 'ascend' : 'descend') : null;

  const columns = [
    {
      title: 'Nº RPS',
      dataIndex: 'numero_rps',
      key: 'numero_rps',
      width: 90,
      sorter: true,
      sortOrder: sortOrder('numero_rps'),
      render: (v) => <Text strong>{v}</Text>,
    },
    {
      title: 'Fatura',
      dataIndex: 'id_origem',
      key: 'id_origem',
      width: 80,
      render: (v) => `#${v}`,
    },
    {
      title: 'Tomador',
      dataIndex: 'tomador_nome',
      key: 'tomador',
      sorter: true,
      sortOrder: sortOrder('tomador'),
    },
    {
      title: 'Competência',
      dataIndex: 'competencia',
      key: 'competencia',
      width: 110,
      sorter: true,
      sortOrder: sortOrder('competencia'),
    },
    {
      title: 'Valor (R$)',
      dataIndex: 'valor_servicos',
      key: 'valor',
      width: 110,
      align: 'right',
      sorter: true,
      sortOrder: sortOrder('valor'),
      render: (v) => parseFloat(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
    },
    {
      title: 'ISS (R$)',
      dataIndex: 'valor_iss',
      key: 'valor_iss',
      width: 100,
      align: 'right',
      render: (v) => parseFloat(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      sorter: true,
      sortOrder: sortOrder('status'),
      render: (v) => <Tag color={STATUS_COLOR[v] || 'default'}>{v}</Tag>,
    },
    {
      title: 'NFS-e',
      dataIndex: 'numero_nfse',
      key: 'numero_nfse',
      width: 90,
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      title: 'Ações',
      key: 'acoes',
      width: 200,
      render: (_, row) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => handleVerDetalhe(row.id)}>
            Ver
          </Button>
          {row.status === 'RPS_PENDENTE' && (
            <>
              <Button
                size="small"
                type="primary"
                icon={<SendOutlined />}
                loading={emitindoId === row.id}
                onClick={() => handleEmitir(row)}
              >
                Emitir NFS-e
              </Button>
              <Button
                size="small"
                danger
                icon={<StopOutlined />}
                loading={cancelandoId === row.id}
                onClick={() => handleCancelar(row)}
              >
                Cancelar
              </Button>
            </>
          )}
          {row.status === 'ERRO' && (
            <Button
              size="small"
              icon={<ReloadOutlined />}
              loading={reenviandoId === row.id}
              onClick={() => handleReenviar(row)}
            >
              Tentar novamente
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const temFiltroAtivo = Object.values(filtros).some(v => v !== '') || filtroStatus !== '';

  return (
    <div>
      <h2 style={{ marginBottom: 16 }}>Notas Fiscais / RPS</h2>

      {/* Filtros de status rápidos */}
      <Space style={{ marginBottom: 12 }} wrap>
        <Button
          onClick={() => { setFiltroStatus('RPS_PENDENTE'); setPage(1); }}
          type={filtroStatus === 'RPS_PENDENTE' ? 'primary' : 'default'}
        >
          Pendentes
        </Button>
        <Button
          onClick={() => { setFiltroStatus('EMITIDO'); setPage(1); }}
          type={filtroStatus === 'EMITIDO' ? 'primary' : 'default'}
        >
          Emitidos
        </Button>
        <Button
          onClick={() => { setFiltroStatus('ERRO'); setPage(1); }}
          type={filtroStatus === 'ERRO' ? 'primary' : 'default'}
        >
          Com Erro
        </Button>
        <Button
          onClick={() => { setFiltroStatus(''); setPage(1); }}
          type={filtroStatus === '' && !temFiltroAtivo ? 'primary' : 'default'}
        >
          Todos
        </Button>
      </Space>

      {/* Filtros avançados */}
      <div style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 6, padding: '12px 16px', marginBottom: 16 }}>
        <Row gutter={[8, 8]} align="bottom">
          <Col xs={24} sm={8} md={3}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 2 }}>Competência exata</div>
            <Input
              placeholder="YYYY-MM"
              value={draft.competencia}
              onChange={e => handleDraft('competencia', e.target.value)}
              onPressEnter={handleBuscar}
              maxLength={7}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={8} md={3}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 2 }}>Comp. inicial</div>
            <Input
              placeholder="YYYY-MM"
              value={draft.competencia_de}
              onChange={e => handleDraft('competencia_de', e.target.value)}
              onPressEnter={handleBuscar}
              maxLength={7}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={8} md={3}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 2 }}>Comp. final</div>
            <Input
              placeholder="YYYY-MM"
              value={draft.competencia_ate}
              onChange={e => handleDraft('competencia_ate', e.target.value)}
              onPressEnter={handleBuscar}
              maxLength={7}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={8} md={5}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 2 }}>Tomador (nome)</div>
            <Input
              placeholder="Nome do tomador"
              value={draft.busca}
              onChange={e => handleDraft('busca', e.target.value)}
              onPressEnter={handleBuscar}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={12} sm={6} md={2}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 2 }}>Nº RPS</div>
            <Input
              placeholder="Ex.: 123"
              value={draft.numero_rps}
              onChange={e => handleDraft('numero_rps', e.target.value)}
              onPressEnter={handleBuscar}
              maxLength={20}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={12} sm={6} md={3}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 2 }}>Nº NFS-e</div>
            <Input
              placeholder="Ex.: 456"
              value={draft.numero_nfse}
              onChange={e => handleDraft('numero_nfse', e.target.value)}
              onPressEnter={handleBuscar}
              maxLength={30}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={12} sm={6} md={2}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 2 }}>Fatura de origem</div>
            <Input
              placeholder="Ex.: 42"
              value={draft.id_fatura}
              onChange={e => handleDraft('id_fatura', e.target.value)}
              onPressEnter={handleBuscar}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} md={3}>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleBuscar}>
                Buscar
              </Button>
              {temFiltroAtivo && (
                <Button icon={<ClearOutlined />} onClick={handleLimpar}>
                  Limpar
                </Button>
              )}
            </Space>
          </Col>
        </Row>
      </div>

      <Table
        rowKey="id"
        dataSource={rps}
        columns={columns}
        loading={loading}
        onChange={handleTableChange}
        pagination={{
          current: page,
          pageSize: 50,
          total,
          onChange: (p) => setPage(p),
          showTotal: (t) => `${t} registros`,
        }}
        scroll={{ x: 1100 }}
      />

      <Modal
        title={detalheRps ? `Detalhes do RPS #${detalheRps.numero_rps}` : 'Detalhes'}
        open={detalheVisible}
        onCancel={() => setDetalheVisible(false)}
        footer={<Button onClick={() => setDetalheVisible(false)}>Fechar</Button>}
        width={720}
      >
        {detalheRps && (
          <>
            <Descriptions size="small" bordered column={2} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Nº RPS">{detalheRps.numero_rps}</Descriptions.Item>
              <Descriptions.Item label="Série">{detalheRps.serie_rps}</Descriptions.Item>
              <Descriptions.Item label="Competência">{detalheRps.competencia}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={STATUS_COLOR[detalheRps.status]}>{detalheRps.status}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Fatura de Origem">#{detalheRps.id_origem}</Descriptions.Item>
              <Descriptions.Item label="NFS-e">{detalheRps.numero_nfse || '—'}</Descriptions.Item>
              {detalheRps.codigo_verificacao && (
                <Descriptions.Item label="Cód. Verificação" span={2}>
                  <Text copyable>{detalheRps.codigo_verificacao}</Text>
                </Descriptions.Item>
              )}
            </Descriptions>

            <Descriptions size="small" bordered column={2} title="Prestador" style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Razão Social" span={2}>{detalheRps.prestador_razao}</Descriptions.Item>
              <Descriptions.Item label="CNPJ">{detalheRps.prestador_cnpj}</Descriptions.Item>
              <Descriptions.Item label="Inscrição Municipal">{detalheRps.prestador_im}</Descriptions.Item>
              <Descriptions.Item label="Código Serviço">{detalheRps.prestador_codigo_servico}</Descriptions.Item>
              <Descriptions.Item label="Alíquota ISS">
                {(parseFloat(detalheRps.prestador_aliquota_iss) * 100).toFixed(2)}%
              </Descriptions.Item>
            </Descriptions>

            <Descriptions size="small" bordered column={2} title="Tomador" style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Nome" span={2}>{detalheRps.tomador_nome}</Descriptions.Item>
              <Descriptions.Item label="CPF/CNPJ">{detalheRps.tomador_cpf_cnpj || '—'}</Descriptions.Item>
              <Descriptions.Item label="E-mail">{detalheRps.tomador_email || '—'}</Descriptions.Item>
              <Descriptions.Item label="Cidade">{detalheRps.tomador_cidade || '—'}</Descriptions.Item>
              <Descriptions.Item label="UF">{detalheRps.tomador_uf || '—'}</Descriptions.Item>
            </Descriptions>

            <Descriptions size="small" bordered column={2} title="Valores">
              <Descriptions.Item label="Valor Serviços">
                {parseFloat(detalheRps.valor_servicos).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              </Descriptions.Item>
              <Descriptions.Item label="Valor ISS">
                {parseFloat(detalheRps.valor_iss).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              </Descriptions.Item>
              <Descriptions.Item label="Alíquota ISS">
                {(parseFloat(detalheRps.aliquota_iss) * 100).toFixed(2)}%
              </Descriptions.Item>
            </Descriptions>

            {detalheRps.eventos?.length > 0 && (
              <>
                <h4 style={{ marginTop: 16 }}>Histórico de Eventos</h4>
                <Table
                  size="small"
                  rowKey="id"
                  dataSource={detalheRps.eventos}
                  pagination={false}
                  columns={[
                    { title: 'Evento', dataIndex: 'tipo_evento', key: 'tipo_evento', width: 200 },
                    { title: 'Anterior', dataIndex: 'status_anterior', key: 'status_anterior', width: 120 },
                    { title: 'Novo', dataIndex: 'status_novo', key: 'status_novo', width: 120 },
                    { title: 'Mensagem', dataIndex: 'mensagem', key: 'mensagem' },
                    {
                      title: 'Data', dataIndex: 'created_at', key: 'created_at', width: 160,
                      render: (v) => v ? new Date(v).toLocaleString('pt-BR') : '—',
                    },
                  ]}
                />
              </>
            )}
          </>
        )}
      </Modal>
    </div>
  );
};

export default NotasFiscais;
